import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import {
  BsStarFill,
  BsStarHalf,
  BsStar,
  BsFillHeartFill,
} from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWishlist,
  addItemToWishlist,
  removeItemFromWishlist,
} from "../../redux/thunks/wishlistThunks";
import { addToCart, fetchCart, removeFromCart } from "../../redux/thunks/cartThunks";
import { toast } from "react-toastify";
import Header from "../Header/Header";
import ProductImageGallery from "./Components/ProductImageGallery";
import QuantitySelector from "./Components/QuantitySelector";
import ReviewForm from "./Components/ReviewForm";
import ReviewList from "./Components/ReviewList";
import Breadcrumb from "./Components/Breadcrumb";
import Footer from "../../Components/Footer/Footer";
import {
  FacebookShareButton, TwitterShareButton, WhatsappShareButton, EmailShareButton,
  FacebookIcon, TwitterIcon, WhatsappIcon, EmailIcon,
} from "react-share";
import { FaShareAlt, FaRegCopy, FaCheck, FaTrash, FaShoppingCart, FaTruck } from "react-icons/fa";
import { globalLogout } from "../../Utils/Logout";

const LoginPromptModal = ({ isOpen, onClose, onConfirm, message }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full">
        <h3 className="text-lg font-medium mb-4">{message}</h3>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
          >Cancel</button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-md hover:from-purple-700 hover:to-blue-600 transition-all duration-300 shadow-md"
          >Login</button>
        </div>
      </div>
    </div>
  );
};

function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

const SocialShare = ({ product }) => {
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareUrl = window.location.href;
  const title = `Check out ${product.name} on our store! Uresia`;
  const popoverRef = useRef();

  useOnClickOutside(popoverRef, () => setShowShareOptions(false));

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="relative">
      <button
        className="flex items-center gap-2 font-medium border border-gray-200 bg-white hover:bg-blue-50 px-3 py-2 rounded-lg shadow-sm transition-all duration-200 focus:ring-2 focus:ring-blue-200 hover:shadow-md"
        onClick={() => setShowShareOptions((open) => !open)}
        aria-label="Share"
      >
        <FaShareAlt className="text-lg text-blue-600" />
        <span className="hidden sm:inline text-gray-700">Share</span>
      </button>
      {showShareOptions && (
        <div
          ref={popoverRef}
          className="absolute z-20 right-0 mt-3 min-w-[210px] max-w-fit bg-white border border-gray-200 rounded-lg shadow-xl p-4 animate-fade-in"
        >
          <div className="absolute -top-2 right-6 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white" />
          <p className="text-gray-900 text-sm font-medium pb-1 pl-1">Share this product</p>
          <div className="flex gap-2 mb-3">
            <FacebookShareButton url={shareUrl} quote={title}><FacebookIcon size={36} round /></FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={title}><TwitterIcon size={36} round /></TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={title}><WhatsappIcon size={36} round /></WhatsappShareButton>
            <EmailShareButton url={shareUrl} subject={title} body={shareUrl}>
              <EmailIcon size={36} round />
            </EmailShareButton>
          </div>
          <button
            onClick={handleCopy}
            type="button"
            className={`flex items-center gap-1 px-3 py-2 w-full rounded-md border border-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-200 font-medium text-gray-700 text-sm transition-all duration-200`}
          >
            {copied ? (
              <>
                <FaCheck className="text-green-500 mr-1" />
                Copied!
              </>
            ) : (
              <>
                <FaRegCopy className="mr-1 text-gray-600" />
                Copy Link
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

const ServiceabilityCheck = ({ productId, variantId, onServiceabilityResult }) => {
  const [pincode, setPincode] = useState("");
  const [paymentType, setPaymentType] = useState("cod");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleCheckServiceability = async () => {
    if (!pincode || pincode.length !== 6) {
      toast.error("Please enter a valid 6-digit pincode");
      return;
    }
    
    setChecking(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/check-serviceability`,
        {
          productId,
          variantId,
          pincode: parseInt(pincode),
          paymentType
        },
        { 
          headers: { 
            Authorization: `Bearer ${Cookies.get("ecom_user_token")}` 
          } 
        }
      );
      
      setResult(response.data);
      if (onServiceabilityResult) {
        onServiceabilityResult(response.data);
      }
      
      if (response.data.deliverable) {
        toast.success(`Product is deliverable to ${pincode}`);
      } else {
        toast.error(`Product not deliverable to ${pincode}`);
      }
    } catch (err) {
      globalLogout(err)
      setError(err.response?.data?.message || "Failed to check serviceability");
      // toast.error(err.response?.data?.message || "Failed to check serviceability");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
      <h3 className="text-lg font-semibold mb-2 flex items-center">
        <FaTruck className="mr-2" /> Check Delivery Availability
      </h3>
      
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          type="text"
          placeholder="Enter 6-digit pincode"
          value={pincode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '');
            if (value.length <= 6) setPincode(value);
          }}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        
        <select
          value={paymentType}
          onChange={(e) => setPaymentType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="cod">Cash on Delivery</option>
          <option value="prepaid">Prepaid</option>
        </select>
        
        <button
          onClick={handleCheckServiceability}
          disabled={checking || pincode.length !== 6}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {checking ? "Checking..." : "Check"}
        </button>
      </div>
      
      {result && (
        <div className={`p-3 rounded-md ${result.deliverable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {result.deliverable ? (
            <div>
              <p className="font-medium">✓ Deliverable to {pincode}</p>
              {result.courier && (
                <div className="mt-2 text-sm">
                  <p>Courier: {result.courier.name}</p>
                  <p>Shipping: ₹{result.courier.freightCharge?.toFixed(2) || '0.00'}</p>
                  {paymentType === "cod" && result.courier.codCharge && (
                    <p>COD Charge: ₹{result.courier.codCharge.toFixed(2)}</p>
                  )}
                  <p className="font-medium mt-1">
                    Total Charges: ₹{result.courier.total?.toFixed(2) || '0.00'}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="font-medium">✗ Not deliverable to {pincode}</p>
          )}
        </div>
      )}
      
      {error && (
        <div className="p-3 bg-red-100 text-red-800 rounded-md mt-2">
          {error}
        </div>
      )}
    </div>
  );
};

function getOptionOrder(product) {
  const availableVariants = product?.variants?.filter(v => v.status !== 'discontinued') || [];
  if (availableVariants.length === 0) return [];
  return availableVariants[0].attributes.map(attr => attr.option.name);
}

function getOptionsForStep(product, optionOrder, selectedAttrs, stepIndex) {
  if (!product?.variants) return [];
  let filteredVariants = product.variants.filter(v => v.status !== 'discontinued');
  optionOrder.slice(0, stepIndex).forEach((name) => {
    if (selectedAttrs[name] !== undefined) {
      filteredVariants = filteredVariants.filter(variant => {
        const attr = variant.attributes.find(a => a.option.name === name);
        return attr && attr.value === selectedAttrs[name];
      });
    }
  });
  const optionName = optionOrder[stepIndex];
  const valuesSet = new Set();
  filteredVariants.forEach(variant => {
    const attr = variant.attributes.find(a => a.option.name === optionName);
    if (attr) {
      if (optionName.toLowerCase() === "color") {
        const withImage = { value: attr.value, image: variant.images?.[0] || null };
        valuesSet.add(JSON.stringify(withImage));
      } else {
        valuesSet.add(attr.value);
      }
    }
  });
  return optionName.toLowerCase() === "color"
    ? Array.from(valuesSet).map(s => JSON.parse(s))
    : Array.from(valuesSet);
}

function getMatchingVariant(product, optionOrder, selectedAttrs) {
  if (!product?.variants) return null;
  return product.variants.find(variant => {
    if (variant.status === "discontinued") return false;
    return optionOrder.every(
      optionName => {
        const attr = variant.attributes.find(a => a.option.name === optionName);
        return attr && selectedAttrs[optionName] === attr.value;
      }
    );
  });
}

function getFirstAvailableAttributeCombo(product, optionOrder) {
  let attrs = {};
  for (let i = 0; i < optionOrder.length; ++i) {
    const values = getOptionsForStep(product, optionOrder, attrs, i);
    if (!values.length) break;
    const v = optionOrder[i].toLowerCase() === 'color' ? values[0].value : values[0];
    attrs[optionOrder[i]] = v;
  }
  return attrs;
}

const ProductDetails = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const [loadingWishlistItem, setLoadingWishlistItem] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [removingFromCart, setRemovingFromCart] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [actionAfterLogin, setActionAfterLogin] = useState(null);
  const [serviceabilityResult, setServiceabilityResult] = useState(null);

  const reviewsPerPage = 3;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector(state => state.cart.items);
  const token = Cookies.get("ecom_user_token");
  const { items: wishlist, loading: wishlistLoading } = useSelector(state => state.wishlist);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch product details
        const productResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/seller/product/getById/${productId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProduct(productResponse.data.product);

        // Fetch reviews
        const reviewsResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/rating/productRating/${productId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setReviews(reviewsResponse.data.ratings || []);

        // Fetch cart if user is logged in
        if (token) {
          await dispatch(fetchCart());
        }

        // Fetch wishlist if user is logged in
        if (token) {
          await dispatch(fetchWishlist());
        }
      } catch (err) {
        setError("Failed to load product details. Please try again.");
        globalLogout(err)

      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [productId, token, dispatch]);

  useEffect(() => {
    if (product && product.category) {
      const buildBreadcrumb = (category, items = []) => {
        if (category) {
          items.unshift({ name: category.name, id: category._id });
          if (category.parent) return buildBreadcrumb(category.parent, items);
        }
        return items;
      };
      const breadcrumb = [
        { name: "Home", path: "/" },
        ...buildBreadcrumb(product.category).map((item) => ({
          ...item,
          path: `/category/${item.id}`,
        })),
        { name: product.name, current: true }
      ];
      setBreadcrumbItems(breadcrumb);
    }
  }, [product]);

  const optionOrder = product ? getOptionOrder(product) : [];

  useEffect(() => {
    if (!product || !optionOrder.length) return;
    if (Object.keys(selectedAttributes).length === 0) {
      const attrs = getFirstAvailableAttributeCombo(product, optionOrder);
      setSelectedAttributes(attrs);
    }
  }, [product]);

  useEffect(() => {
    if (!product || !optionOrder.length) return;
    const variant = getMatchingVariant(product, optionOrder, selectedAttributes);
    setSelectedVariant(variant ? variant : null);
    setCurrentImageIndex(0);
    // Reset serviceability result when variant changes
    setServiceabilityResult(null);
  }, [selectedAttributes, product, optionOrder]);

  useEffect(() => {
    if (selectedVariant && cartItems) {
      const cartItem = cartItems.find(item => item.variantId === selectedVariant._id);
      setQuantity(cartItem ? cartItem.quantity : 1);
    }
  }, [selectedVariant, cartItems]);

  const cartItem = selectedVariant 
    ? cartItems.find(item => item.variantId === selectedVariant._id) 
    : null;
  
  const currentCartQuantity = cartItem ? cartItem.quantity : 0;
  const isItemInCart = !!cartItem;
  const quantityUnchanged = isItemInCart && quantity === currentCartQuantity;

  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = reviews.slice(indexOfFirstReview, indexOfLastReview);
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleReviewSubmit = async ({ rating, reviewText, reviewImages }) => {
    try {
      const formData = new FormData();
      formData.append('ratings', rating);
      formData.append('reviews', reviewText);
      formData.append('productId', productId);
      if (reviewImages && reviewImages.length > 0) {
        Array.from(reviewImages).forEach(file => {
          formData.append('productImages', file);
        });
      }
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/rating/addProductRating`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/rating/productRating/${productId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReviews(response.data.ratings || []);
      toast.success("Thank you for your review!");
    } catch (error) {
      // // if (error?.response?.data?.message) toast.error(error.response.data.message);
      // else toast.error("Failed to submit review. Please try again.");
      globalLogout(error)
    }
  };

  const handleAttributeSelect = (optionName, value, stepIndex) => {
    let newAttributes = {};
    optionOrder.slice(0, stepIndex).forEach(name => {
      if (selectedAttributes[name] !== undefined) newAttributes[name] = selectedAttributes[name];
    });
    newAttributes[optionName] = value;
    for (let nextStep = stepIndex + 1; nextStep < optionOrder.length; ++nextStep) {
      const nextOption = optionOrder[nextStep];
      const possibleValues = getOptionsForStep(product, optionOrder, newAttributes, nextStep);
      if (!possibleValues.length) break;
      const v = nextOption.toLowerCase() === 'color' ? possibleValues[0].value : possibleValues[0];
      newAttributes[nextOption] = v;
    }
    setSelectedAttributes(newAttributes);
  };

  const isSelectionAvailable = () => !!getMatchingVariant(product, optionOrder, selectedAttributes);

  const handleServiceabilityResult = (result) => {
    setServiceabilityResult(result);
  };

  const handleAddToCart = async () => {
    if (!token) {
      setModalMessage("Please login to add items to your cart");
      setActionAfterLogin("cart");
      setShowLoginModal(true);
      return;
    }
    if (!selectedVariant) {
      toast.error("Please select an available variant");
      return;
    }
    if (selectedVariant.status === "discontinued") {
      toast.error("This variant is no longer available");
      return;
    }
    if (selectedVariant.stock <= 0) {
      toast.error("This variant is out of stock");
      return;
    }
    try {
      setAddingToCart(true);
      await dispatch(
        addToCart({
          productId: product._id,
          variantId: selectedVariant._id,
          quantity,
        })
      );
      // Fetch updated cart after adding
      await dispatch(fetchCart());
      toast.success(`${quantity} ${quantity > 1 ? "items" : "item"} added to cart`);
    } catch (error) {
      toast.error("Failed to add to cart. Please try again.");
    } finally {
      setAddingToCart(false);
    }
  };

  const handleRemoveFromCart = async () => {
    if (!token) {
      setModalMessage("Please login to manage your cart");
      setShowLoginModal(true);
      return;
    }
    if (!selectedVariant) return;
    
    try {
      setRemovingFromCart(true);
      await dispatch(removeFromCart({
        productId: product._id,
        variantId: selectedVariant._id
      }));
      // Fetch updated cart after removal
      await dispatch(fetchCart());
      toast.success("Item removed from cart");
    } catch (error) {
      toast.error("Failed to remove from cart. Please try again.");
    } finally {
      setRemovingFromCart(false);
    }
  };

  const toggleWishlist = async () => {
    if (!token) {
      setModalMessage("You need to login to add items to your wishlist");
      setActionAfterLogin("wishlist");
      setShowLoginModal(true);
      return;
    }
    try {
      setLoadingWishlistItem(true);
      const existingItem = wishlist.find(
        item => item.productId._id === product._id && 
                item.variantId === selectedVariant?._id
      );
      if (existingItem) {
        await dispatch(removeItemFromWishlist(product._id, selectedVariant._id));
        toast.success("Removed from wishlist");
      } else {
        if (!selectedVariant) {
          toast.error("Please select a variant first");
          return;
        }
        await dispatch(addItemToWishlist(product._id, selectedVariant._id));
        toast.success("Added to wishlist");
      }
    } catch (error) {
      toast.error("Failed to update wishlist. Please try again.");
      console.error("Wishlist error:", error);
    } finally {
      setLoadingWishlistItem(false);
    }
  };

  const handleLoginConfirm = () => {
    setShowLoginModal(false);
    navigate("/login", { state: { from: window.location.pathname } });
  };
  const handleLoginCancel = () => setShowLoginModal(false);

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    for (let i = 0; i < fullStars; i++) stars.push(<BsStarFill key={`full-${i}`} className="text-yellow-400" />);
    if (hasHalfStar) stars.push(<BsStarHalf key="half" className="text-yellow-400" />);
    for (let i = 0; i < 5 - stars.length; i++) stars.push(<BsStar key={`empty-${i}`} className="text-yellow-400" />);
    return stars;
  };

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.ratings, 0);
    return (sum / reviews.length).toFixed(1);
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );

  if (error)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-red-500 text-lg">{error}</div>
      </div>
    );

  if (!product)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-gray-500 text-lg">Product not found</div>
      </div>
    );

  // Check if product is published and adminPublished
  if (!product.published || !product.adminPublished) {
    return (
      <>
        <Header />
        <div className="flex justify-center items-center h-[calc(100vh-80px)]">
          <div className="text-gray-500 text-lg">This product is not currently available</div>
        </div>
      </>
    );
  }

  const allVariantsDiscontinued = product?.variants?.every(v => v.status === 'discontinued');
  if (allVariantsDiscontinued) {
    return (
      <>
        <Header />
        <div className="flex justify-center items-center h-[calc(100vh-80px)]">
          <div className="text-gray-500 text-lg">This product is no longer available</div>
        </div>
      </>
    );
  }

  // Button text logic
  let addToCartButtonText;
  if (addingToCart) {
    addToCartButtonText = isItemInCart ? "Updating..." : "Adding...";
  } else if (selectedVariant?.status === "discontinued") {
    addToCartButtonText = "Variant Discontinued";
  } else if (selectedVariant?.stock <= 0) {
    addToCartButtonText = "Out of Stock";
  } else if (isItemInCart) {
    addToCartButtonText = quantityUnchanged ? "In Cart" : "Update Cart";
  } else {
    addToCartButtonText = "Add to Cart";
  }

  return (
    <>
      <Header />
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb items={breadcrumbItems} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Images */}
          <div>
            <ProductImageGallery
              images={selectedVariant?.images || product.variants?.[0]?.images || []}
              currentImageIndex={currentImageIndex}
              onNextImage={() =>
                setCurrentImageIndex(prev =>
                  prev < ((selectedVariant?.images?.length || product.variants?.[0]?.images?.length || 1) - 1)
                    ? prev + 1 : 0)}
              onPrevImage={() =>
                setCurrentImageIndex(prev =>
                  prev > 0
                    ? prev - 1
                    : (selectedVariant?.images?.length || product.variants?.[0]?.images?.length || 1) - 1)}
              onThumbnailClick={index => setCurrentImageIndex(index)}
            />
          </div>

          {/* Main content & purchase */}
          <div className="flex flex-col h-full">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">{product.name}</h1>
            
            {/* Seller Info */}
            {product.seller?.businessName && (
              <div className="text-sm text-gray-500 mb-2">
                Sold by: <span className="font-semibold text-gray-700">{product.seller.businessName}</span>
              </div>
            )}

            <div className="flex items-center mb-4">
              <div className="flex mr-2">{renderRatingStars(calculateAverageRating())}</div>
              <span className="text-sm text-gray-500">({reviews.length} reviews)</span>
            </div>

            {optionOrder.map((optionName, stepIndex) => {
              const showOption =
                stepIndex === 0 || optionOrder.slice(0, stepIndex).every(opt => selectedAttributes[opt]);
              if (!showOption) return null;
              const possibleValues = getOptionsForStep(product, optionOrder, selectedAttributes, stepIndex);
              if (possibleValues.length === 0) return null;
              return (
                <div key={optionName} className="mb-4">
                  <h3 className="text-lg font-semibold mb-2">{optionName}</h3>
                  <div className="flex flex-wrap gap-2">
                    {possibleValues.map((valueObj, index) => {
                      const value =
                        optionName.toLowerCase() === "color"
                          ? valueObj.value
                          : valueObj;
                      const image =
                        optionName.toLowerCase() === "color"
                          ? valueObj.image
                          : null;
                      return (
                        <button
                          key={index}
                          onClick={() => handleAttributeSelect(optionName, value, stepIndex)}
                          className={`px-3 py-2 rounded-md border flex flex-col items-center transition-all duration-200
                            ${selectedAttributes[optionName] === value
                              ? "bg-gradient-to-r from-purple-600 to-blue-500 text-white border-transparent shadow-md"
                              : "bg-white text-gray-800 border-gray-300 hover:border-blue-500 hover:shadow-md"}`}
                        >
                          {image && (
                            <img
                              src={image}
                              alt={value}
                              className="w-12 h-12 object-cover rounded-sm mb-1"
                            />
                          )}
                          <span>{value}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {optionOrder.length > 0 &&
              Object.keys(selectedAttributes).length === optionOrder.length &&
              !isSelectionAvailable() && (
                <div className="mb-4 p-3 rounded-md bg-yellow-100 text-yellow-800">
                  {"This combination is not available"}
                </div>
              )}

            {selectedVariant && isSelectionAvailable() ? (
              <>
                <div className="flex items-center flex-wrap gap-4 mb-3">
                  <span className="text-3xl font-bold text-gray-800">
                    ₹{selectedVariant.price.toFixed(2)}
                  </span>
                  {selectedVariant.mrp && (
                    <span className="ml-2 text-lg text-gray-500 line-through">
                      ₹{selectedVariant.mrp.toFixed(2)}
                    </span>
                  )}
                  {selectedVariant.discount && (
                    <span className="ml-2 text-green-600 font-medium bg-green-100 px-2 py-1 rounded-full text-sm">
                      {selectedVariant.discount}% OFF
                    </span>
                  )}
                  <div className="ml-auto">
                    <SocialShare product={product} />
                  </div>
                </div>

                <div className="mb-4">
                  <h3 className="text-lg font-semibold mb-2">Description</h3>
                  <p className="text-gray-600">{product.description}</p>
                </div>

                {product.specifications && product.specifications.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-2">Specifications</h3>
                    <div className="space-y-2">
                      {product.specifications.map((spec, idx) => (
                        <div key={idx} className="flex">
                          <span className="font-medium text-gray-700 w-1/3">{spec.label}:</span>
                          <span className="text-gray-600 flex-1">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-6 space-y-4">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold mb-2">Availability</h3>
                    <p className={
                      selectedVariant?.status === "discontinued"
                        ? "text-red-600"
                        : selectedVariant?.stock > 0
                          ? "text-green-600 font-medium"
                          : "text-red-600"
                    }>
                      {selectedVariant?.status === "discontinued"
                        ? "This variant is discontinued"
                        : selectedVariant?.stock > 0
                          ? `In Stock`
                          : "Out of Stock"}
                    </p>
                  </div>

                  <div className="w-full">
                    <h3 className="text-lg font-semibold mb-2">Quantity</h3>
                    <QuantitySelector
                      quantity={quantity}
                      setQuantity={setQuantity}
                      maxStock={selectedVariant.stock}
                      disabled={addingToCart || selectedVariant.status === "discontinued"}
                    />
                  </div>

                  {/* Serviceability Check */}
                  <ServiceabilityCheck
                    productId={productId}
                    variantId={selectedVariant._id}
                    onServiceabilityResult={handleServiceabilityResult}
                  />

                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    {token ? (
                      <>
                        <div className="flex-1 flex gap-3">
                          <button
                            onClick={handleAddToCart}
                            disabled={
                              addingToCart ||
                              !selectedVariant ||
                              selectedVariant.stock <= 0 ||
                              selectedVariant.status === "discontinued" ||
                              (isItemInCart && quantityUnchanged)
                            }
                            className={`flex-1 py-3 px-6 rounded-md font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                              addingToCart ||
                              !selectedVariant ||
                              selectedVariant?.stock <= 0 ||
                              selectedVariant?.status === "discontinued" ||
                              (isItemInCart && quantityUnchanged)
                                ? "bg-gray-300 cursor-not-allowed"
                                : isItemInCart
                                  ? "bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white shadow-md hover:shadow-lg"
                                  : "bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white shadow-md hover:shadow-lg"
                            }`}
                          >
                            {addingToCart ? (
                              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <>
                                <FaShoppingCart />
                                {!selectedVariant || selectedVariant.stock <= 0 || selectedVariant.status === "discontinued"
                                  ? "Unavailable"
                                  : addToCartButtonText}
                              </>
                            )}
                          </button>
                          
                          {isItemInCart && (
                            <button
                              onClick={handleRemoveFromCart}
                              disabled={removingFromCart}
                              className="py-3 px-4 rounded-md font-medium bg-gradient-to-r from-red-600 to-pink-500 hover:from-red-700 hover:to-pink-600 text-white flex items-center justify-center shadow-md hover:shadow-lg transition-all duration-300"
                              title="Remove from cart"
                            >
                              {removingFromCart ? (
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <FaTrash className="text-white" />
                              )}
                            </button>
                          )}
                        </div>
                        
                        <button
                          onClick={toggleWishlist}
                          disabled={wishlistLoading || loadingWishlistItem || !selectedVariant}
                          className={`flex items-center justify-center gap-2 py-3 px-6 rounded-md font-medium border transition-all duration-300 shadow-sm hover:shadow-md ${
                            wishlist.some(
                              item => item.productId._id === product._id && 
                                      item.variantId === selectedVariant?._id
                            )
                              ? "bg-gradient-to-r from-pink-100 to-rose-100 border-pink-300 text-pink-600 hover:from-pink-200 hover:to-rose-200"
                              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {loadingWishlistItem ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <BsFillHeartFill className={
                              wishlist.some(
                                item => item.productId._id === product._id && 
                                        item.variantId === selectedVariant?._id
                              )
                                ? "text-pink-500"
                                : "text-gray-400"
                            } />
                          )}
                          <span>
                            {wishlist.some(
                              item => item.productId._id === product._id && 
                                      item.variantId === selectedVariant?._id
                            )
                              ? "Saved"
                              : "Save"}
                          </span>
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleAddToCart}
                        disabled={
                          !selectedVariant ||
                          selectedVariant.stock <= 0 ||
                          selectedVariant.status === "discontinued"
                        }
                        className={`flex-1 py-3 px-6 rounded-md font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                          !selectedVariant ||
                          selectedVariant.stock <= 0 ||
                          selectedVariant.status === "discontinued"
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white shadow-md hover:shadow-lg"
                        }`}
                      >
                        <FaShoppingCart />
                        {selectedVariant?.status === "discontinued"
                          ? "Variant Discontinued"
                          : selectedVariant?.stock <= 0
                            ? "Out of Stock"
                            : "Add to Cart"}
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-gray-100 p-4 rounded-md text-gray-700">
                {optionOrder.length > 0 &&
                  Object.keys(selectedAttributes).length < optionOrder.length
                  ? "Please select options to see availability"
                  : "Selected combination is not available"}
              </div>
            )}
          </div>
        </div>

        {/* Reviews & review form */}
        {showReviewForm && (
          <ReviewForm
            productId={productId}
            onClose={() => setShowReviewForm(false)}
            onReviewSubmit={handleReviewSubmit}
          />
        )}
        <div className="mb-12 mt-16">
          <button
            onClick={() => {
              if (token) {
                setShowReviewForm(true);
              } else {
                setModalMessage("Please login to write a review");
                setShowLoginModal(true);
              }
            }}
            className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white py-2 px-4 rounded-md shadow-md hover:shadow-lg transition-all duration-300"
          >
            Write a Review
          </button>
          <ReviewList
            reviews={currentReviews}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={paginate}
            onOpenReviewForm={() => {
              if (token) {
                setShowReviewForm(true);
              } else {
                setModalMessage("Please login to write a review");
                setShowLoginModal(true);
              }
            }}
          />
        </div>
        
      </div>

      <Footer />
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={handleLoginCancel}
        onConfirm={handleLoginConfirm}
        message={modalMessage}
      />
    </>
  );
};

export default ProductDetails;

